import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { digObject } from '@domain-group/javascript-utils'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import useCreateOrEditPublicationKeyContactModal from './hooks/useCreateOrEditPublicationKeyContactModal'

const confirmDelete = (updatePublication, updateFn, preparePublication, closeModal) => {
  swal
    .fire({
      title: 'Delete Contact?',
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it.',
      confirmButtonColor: '#DD6B55',
    })
    .then(({ value }) => {
      if (value){
        updatePublication({
          callbacks: {
            action: updateFn,
            afterAction: closeModal,
          },
          entityParams: preparePublication('delete'),
          toastText: 'Key Contact Deleted',
        })
      }
    })
}

const CreateOrEditPublicationKeyContactModal = (props) => {
  const { callbacks, modalKey, showModal } = props
  const { closeModal, updatePublication } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { publication } = modalPayload

  const keyContact = digObject(modalPayload, 'keyContact', {})

  const {
    callbacks: {
      preparePublication,
    },
    errors,
    saveEnabled,
    entityState,
    handlers,
    updateFn,
    updating,
  } = useCreateOrEditPublicationKeyContactModal({ keyContact, publication })

  const {
    contact_email,
    contact_name,
    contact_number,
    contact_type,
  } = entityState

  const actionText = keyContact.id ? 'Updated' : 'Created'

  const updatePublicationPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: preparePublication(),
    toastText: `Key Contact ${actionText}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Key" titleSecondLine="Contacts" />

      <SidebarModal.Content>
        <Form>
          <Form.Field errorMessage={errors.contact_name} label="*Contact Name">
            <input
              name="contact_name"
              type="text"
              value={contact_name}
              {...handlers}
            />
          </Form.Field>

          <Form.Field errorMessage={errors.contact_number} label="*Contact Number" marginTop="large">
            <input
              name="contact_number"
              type="text"
              value={contact_number}
              {...handlers}
            />
          </Form.Field>

          <Form.Field errorMessage={errors.contact_email} label="*Contact Email" marginTop="large">
            <input
              name="contact_email"
              type="text"
              value={contact_email}
              {...handlers}
            />
          </Form.Field>

          <Form.Field errorMessage={errors.contact_type} label="*Type" marginTop="large">
            <select
              name="contact_type"
              value={contact_type}
              {...handlers}
            >
              <option value="">Please Select...</option>
              <option value="artwork">Artwork</option>
              <option value="booking">Booking</option>
            </select>
          </Form.Field>

          {keyContact.id && (
            <Form.Field direction="column" label="More Options" boxProps={{ marginTop: 'large' }}>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={updating}
                onClick={() => confirmDelete(updatePublication, updateFn, preparePublication, closeModal)}
                size="medium"
              >
                Delete Contact
              </Button>
            </Form.Field>
          )}
        </Form>

      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => updatePublication(updatePublicationPayload)}
          size="large"
        >
          {keyContact.id ? 'Update Contact' : 'Create Contact'}
        </Button>
      </SidebarModal.Footer>

    </SidebarModal>
  )
}

CreateOrEditPublicationKeyContactModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

CreateOrEditPublicationKeyContactModal.defaultProps = {
  modalKey: 'CreateOrEditPublicationKeyContactModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditPublicationKeyContactModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
